import {
  faChevronDown,
  faChevronRight,
  faSpinner,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ProductionLine } from '@/api/productions/types';
import { DataTable } from '@/components/shared/DataTable';
import { TagListValue, TextValue } from '@/components/shared/DataTable/cells';
import type { ColumnDef } from '@/components/shared/DataTable/tanstack';
import { FieldCard, FieldRow } from '@/components/shared/FieldLayout';
import {
  PageHeader,
  PageHeaderFooter,
  PageHeaderFooterLeft,
} from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { useTranslator } from '@/hooks/useTranslator';
import { formatDateDisplay } from '@/lib/utils/formatters';
import { useProductionDetail } from '../../../-hooks/useProductionDetail';

export const Route = createFileRoute(
  '/(client)/productions/(pages)/(detail)/$productionId/',
)({ component: ProductionDetailPage });

const EMPTY = <span className='text-xs text-muted-foreground'>—</span>;

// ─── Unified row type ─────────────────────────────────────────────────────────

interface ItemRow {
  id: string;
  isLotRow?: boolean;
  // Product fields (depth 0)
  productName?: string;
  productType?: string;
  code?: string;
  materials?: Array<{ name: string; percentage: number }>;
  totalQty?: number | null;
  totalKgQty?: number | null;
  // Lot fields (depth 1) — mapped to dedicated column slots
  lotName?: string;
  lotQty?: string | null; // "Produced Quantity" slot
  lotKgQty?: string | null; // "Kilogram Qty" slot
  subRows?: ItemRow[];
}

function normaliseLines(lines: ProductionLine[]): ItemRow[] {
  return lines.map(line => ({
    id: line.id,
    productName: line.product?.name,
    productType: line.product?.productCategoryName,
    code: line.product?.code,
    materials: line.product?.materials,
    totalQty: line.totalKgQuantity,
    totalKgQty: line.totalKgQuantity,
    subRows: (line.product?.lots ?? []).map((l, li) => ({
      id: `${line.id}-lot-${li}`,
      isLotRow: true,
      lotName: l.fiberLotName ?? l.name,
      lotQty: l.quantity != null ? `${l.quantity} ${l.unit ?? 'kg'}` : null,
      lotKgQty: l.kgQuantity != null ? `${l.kgQuantity} kg` : null,
    })),
  }));
}

// ─── Columns — dual-purpose: product data at depth 0, lot data at depth 1 ────

function makeColumns(
  isInput: boolean,
  t: (k: string) => string,
): ColumnDef<ItemRow>[] {
  const label = isInput ? t('detail.input') : t('detail.output');
  return [
    {
      id: 'col1',
      // depth 0 → "Product"  |  depth 1 → "Lot"
      header: t('detail.product'),
      cell: ({ row }) => {
        if (row.original.isLotRow) {
          return (
            <span className='text-xs text-muted-foreground pl-8 italic'>
              {row.original.lotName ?? '—'}
            </span>
          );
        }
        return (
          <div className='flex items-center gap-1.5'>
            {row.getCanExpand() && (
              <button
                type='button'
                onClick={row.getToggleExpandedHandler()}
                className='text-muted-foreground hover:text-foreground shrink-0'
              >
                {row.getIsExpanded() ? (
                  <FontAwesomeIcon icon={faChevronDown} className='size-3.5' />
                ) : (
                  <FontAwesomeIcon icon={faChevronRight} className='size-3.5' />
                )}
              </button>
            )}
            <span className='text-xs font-medium'>
              {row.original.productName ?? '—'}
            </span>
          </div>
        );
      },
    },
    {
      id: 'col2',
      // depth 0 → "Type"  |  depth 1 → "Produced Quantity"
      header: t('detail.type'),
      cell: ({ row }) =>
        row.original.isLotRow ? (
          <TextValue value={row.original.lotQty} emptyNode={EMPTY} />
        ) : (
          <TextValue value={row.original.productType} emptyNode={EMPTY} />
        ),
    },
    {
      id: 'col3',
      // depth 0 → "Code"  |  depth 1 → "Kilogram Qty"
      header: t('detail.code'),
      cell: ({ row }) =>
        row.original.isLotRow ? (
          <TextValue value={row.original.lotKgQty} emptyNode={EMPTY} />
        ) : (
          <TextValue value={row.original.code} emptyNode={EMPTY} />
        ),
    },
    {
      id: 'col4',
      // depth 0 → "Composition"  |  depth 1 → "Composition weight" = —
      header: t('detail.composition'),
      cell: ({ row }) => {
        if (row.original.isLotRow) return EMPTY;
        const labels = (row.original.materials ?? []).map(
          m => `${m.percentage}% ${m.name}`,
        );
        return <TagListValue labels={labels} emptyNode={EMPTY} />;
      },
    },
    {
      id: 'col5',
      header: `Total ${label}`,
      cell: ({ row }) =>
        row.original.isLotRow ? (
          EMPTY
        ) : (
          <TextValue
            value={
              row.original.totalQty != null
                ? `${row.original.totalQty} kg`
                : null
            }
            emptyNode={EMPTY}
          />
        ),
    },
    {
      id: 'col6',
      header: `Total ${label} in KG`,
      cell: ({ row }) =>
        row.original.isLotRow ? (
          EMPTY
        ) : (
          <TextValue
            value={
              row.original.totalKgQty != null
                ? `${row.original.totalKgQty} kg`
                : null
            }
            emptyNode={EMPTY}
          />
        ),
    },
    {
      id: 'col7',
      header: t('detail.compositionWeight'),
      cell: () => EMPTY,
    },
  ];
}

function FieldValue({ value }: { value: string | null | undefined }) {
  return <span className='text-xs'>{value ?? '—'}</span>;
}

function ProductionDetailPage() {
  const { productionId } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'productions' });
  const navigate = useNavigate();
  const { data: production, isLoading } = useProductionDetail(productionId);

  if (isLoading)
    return (
      <div className='flex-1 flex items-center justify-center'>
        <FontAwesomeIcon
          icon={faSpinner}
          className='size-5 animate-spin text-muted-foreground'
        />
      </div>
    );
  if (!production)
    return (
      <div className='flex-1 flex flex-col items-center justify-center gap-3'>
        <p className='text-sm text-muted-foreground'>{t('detail.notFound')}</p>
        <Button
          variant='outline'
          onClick={() => void navigate({ to: '/productions' })}
        >
          {t('detail.backToList')}
        </Button>
      </div>
    );

  const outputRows = normaliseLines(production.outputs ?? []);
  const inputRows = normaliseLines(production.inputs ?? []);
  const outputColumns = makeColumns(false, t);
  const inputColumns = makeColumns(true, t);

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      <PageHeader>
        <div className='flex items-center gap-2'>
          <button
            type='button'
            onClick={() => void navigate({ to: '/productions' })}
            className='text-xs text-muted-foreground hover:text-foreground'
          >
            {t('detail.back')}
          </button>
          <span className='text-muted-foreground/50 text-xs'>›</span>
          <h1 className='text-sm font-semibold'>{production.jobId}</h1>
        </div>
        <PageHeaderFooter>
          <PageHeaderFooterLeft>
            {production.productionStartDate && (
              <span>
                <span className='text-muted-foreground/60'>
                  {t('detail.startDate')}:
                </span>{' '}
                <span className='text-foreground'>
                  {formatDateDisplay(production.productionStartDate)}
                </span>
              </span>
            )}
            {production.productionEndDate && (
              <span>
                <span className='text-muted-foreground/60'>
                  {t('detail.endDate')}:
                </span>{' '}
                <span className='text-foreground'>
                  {formatDateDisplay(production.productionEndDate)}
                </span>
              </span>
            )}
            {production.updatedAt && (
              <span>
                <span className='text-muted-foreground/60'>
                  {t('detail.lastUpdated')}:
                </span>{' '}
                <span className='text-foreground'>
                  {formatDateDisplay(production.updatedAt)}
                </span>
              </span>
            )}
          </PageHeaderFooterLeft>
        </PageHeaderFooter>
      </PageHeader>

      <div className='flex flex-col gap-6 px-6 py-6 @container'>
        <div className='grid grid-cols-2 gap-4 items-stretch'>
          <FieldCard title={t('detail.productionInfo')}>
            <FieldRow
              label={
                <span className='text-xs text-muted-foreground'>
                  {t('detail.jobId')}
                </span>
              }
            >
              <FieldValue value={production.jobId} />
            </FieldRow>
            <FieldRow
              label={
                <span className='text-xs text-muted-foreground'>
                  {t('detail.process')}
                </span>
              }
            >
              <FieldValue value={production.fiberProductionProcess?.name} />
            </FieldRow>
            <FieldRow
              label={
                <span className='text-xs text-muted-foreground'>
                  {t('detail.startDate')}
                </span>
              }
            >
              <FieldValue
                value={
                  production.productionStartDate
                    ? formatDateDisplay(production.productionStartDate)
                    : null
                }
              />
            </FieldRow>
            <FieldRow
              label={
                <span className='text-xs text-muted-foreground'>
                  {t('detail.endDate')}
                </span>
              }
            >
              <FieldValue
                value={
                  production.productionEndDate
                    ? formatDateDisplay(production.productionEndDate)
                    : null
                }
              />
            </FieldRow>
            <FieldRow
              label={
                <span className='text-xs text-muted-foreground'>
                  {t('detail.cocModel')}
                </span>
              }
            >
              <FieldValue value={production.cocModel?.name} />
            </FieldRow>
            <FieldRow
              label={
                <span className='text-xs text-muted-foreground'>
                  {t('detail.createdBy')}
                </span>
              }
            >
              <FieldValue value={production.createdByUserName} />
            </FieldRow>
            <FieldRow
              label={
                <span className='text-xs text-muted-foreground'>
                  {t('detail.updatedBy')}
                </span>
              }
            >
              <FieldValue value={production.updatedByUserName} />
            </FieldRow>
          </FieldCard>
          <FieldCard title={t('detail.processDetails')} className='h-full'>
            <FieldRow
              label={
                <span className='text-xs text-muted-foreground'>
                  {t('detail.process')}
                </span>
              }
            >
              <FieldValue value={production.fiberProductionProcess?.name} />
            </FieldRow>
            <FieldRow
              label={
                <span className='text-xs text-muted-foreground'>
                  {t('detail.productType')}
                </span>
              }
            >
              <FieldValue
                value={production.fiberProductionProcess?.productCategoryName}
              />
            </FieldRow>
            <FieldRow
              label={
                <span className='text-xs text-muted-foreground'>
                  {t('detail.facilityWastageFactor')}
                </span>
              }
            >
              <FieldValue
                value={
                  production.fiberProductionProcess?.facilityWastageFactor !=
                  null
                    ? `${production.fiberProductionProcess.facilityWastageFactor.toFixed(2)}%`
                    : null
                }
              />
            </FieldRow>
            <FieldRow
              label={
                <span className='text-xs text-muted-foreground'>
                  {t('detail.wastageFactor')}
                </span>
              }
            >
              <FieldValue
                value={
                  production.wastageFactor != null
                    ? `${production.wastageFactor.toFixed(2)}%`
                    : 'n/a'
                }
              />
            </FieldRow>
          </FieldCard>
        </div>

        <div className='flex flex-col gap-2'>
          <p className='text-sm font-semibold'>{t('detail.outputs')}</p>
          <DataTable
            data={outputRows}
            columns={outputColumns}
            rowIdKey='id'
            getSubRows={row => row.subRows}
            disablePagination
            hideFooter
            hideToolbar
            emptyMessage={t('detail.noOutputs')}
            storageKey='production-outputs'
          />
        </div>

        <div className='flex flex-col gap-2'>
          <p className='text-sm font-semibold'>{t('detail.inputs')}</p>
          <DataTable
            data={inputRows}
            columns={inputColumns}
            rowIdKey='id'
            getSubRows={row => row.subRows}
            disablePagination
            hideFooter
            hideToolbar
            emptyMessage={t('detail.noInputs')}
            storageKey='production-inputs'
          />
        </div>
      </div>
    </div>
  );
}
